let sdkPromise = null
let securityScriptPromise = null
/** @type {Map<string, object>} */
const mpInstances = new Map()

const SDK_URL = 'https://sdk.mercadopago.com/js/v2'
const SECURITY_SCRIPT_URL = 'https://www.mercadopago.com/v2/security.js'
const SDK_LOAD_TIMEOUT_MS = 20000
const DEVICE_ID_WAIT_MS = 15000

/** Garante #deviceId no DOM antes do security.js (doc MP). */
export function ensureMercadoPagoDeviceIdElement() {
  if (typeof document === 'undefined') return null
  let el = document.getElementById('deviceId')
  if (!el) {
    el = document.createElement('input')
    el.type = 'hidden'
    el.id = 'deviceId'
    el.name = 'deviceId'
    el.value = ''
    document.body.appendChild(el)
  }
  return el
}

function waitForMercadoPagoGlobal(timeoutMs = SDK_LOAD_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Mercado Pago SDK requires a browser environment'))
      return
    }
    if (window.MercadoPago) {
      resolve(window.MercadoPago)
      return
    }
    const startedAt = Date.now()
    const tick = () => {
      if (window.MercadoPago) {
        resolve(window.MercadoPago)
        return
      }
      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('Timeout waiting for Mercado Pago SDK'))
        return
      }
      setTimeout(tick, 50)
    }
    tick()
  })
}

function normalizeDeviceSessionValue(value) {
  if (value == null) return ''
  const normalized = String(value).trim()
  if (!normalized || normalized === 'undefined' || normalized === 'null') {
    return ''
  }
  return normalized
}

export function readMercadoPagoDeviceSessionId() {
  if (typeof window === 'undefined') return ''

  const fromGlobal = normalizeDeviceSessionValue(globalThis.MP_DEVICE_SESSION_ID)
  if (fromGlobal) return fromGlobal

  const hidden = document.getElementById('deviceId')
  if (hidden instanceof HTMLInputElement) {
    const fromInput = normalizeDeviceSessionValue(hidden.value)
    if (fromInput) return fromInput
  }

  return ''
}

/** Sincroniza do DOM (security.js no index.html preenche #deviceId de forma assíncrona). */
export function syncMercadoPagoDeviceSessionFromDom() {
  return readMercadoPagoDeviceSessionId()
}

function waitForMercadoPagoDeviceSessionId(timeoutMs = DEVICE_ID_WAIT_MS) {
  return new Promise((resolve) => {
    const startedAt = Date.now()
    const tick = () => {
      const id = readMercadoPagoDeviceSessionId()
      if (id) {
        resolve(id)
        return
      }
      if (Date.now() - startedAt >= timeoutMs) {
        resolve('')
        return
      }
      setTimeout(tick, 100)
    }
    tick()
  })
}

async function waitForSecurityScriptReady(script) {
  if (!script || script.dataset.mpSecurityLoaded === '1') {
    return
  }

  await new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      script.dataset.mpSecurityLoaded = '1'
      resolve()
    }

    script.addEventListener('load', finish, { once: true })
    script.addEventListener('error', finish, { once: true })

    if (readMercadoPagoDeviceSessionId()) {
      finish()
      return
    }

    setTimeout(finish, 100)
  })
}

/**
 * Doc MP: https://www.mercadopago.com.br/developers/pt/docs/checkout-api/how-tos/improve-payment-approval/recommendations
 * Gera MP_DEVICE_SESSION_ID via security.js (view=checkout + input#deviceId).
 */
export function loadMercadoPagoSecurityScript(view = 'checkout') {
  if (typeof window === 'undefined') {
    return Promise.resolve('')
  }

  const existingId = readMercadoPagoDeviceSessionId()
  if (existingId) {
    return Promise.resolve(existingId)
  }

  ensureMercadoPagoDeviceIdElement()

  if (!securityScriptPromise) {
    securityScriptPromise = (async () => {
      try {
        let script = document.querySelector('script[data-mp-security="v2"]')
          || document.querySelector('script[src*="mercadopago.com/v2/security.js"]')
        if (!script) {
          script = document.createElement('script')
          script.src = SECURITY_SCRIPT_URL
          script.async = false
          script.setAttribute('view', view)
          script.dataset.mpSecurity = 'v2'
          await new Promise((resolve, reject) => {
            script.addEventListener('load', resolve, { once: true })
            script.addEventListener('error', reject, { once: true })
            document.head.appendChild(script)
          })
        } else {
          await waitForSecurityScriptReady(script)
        }

        return waitForMercadoPagoDeviceSessionId(DEVICE_ID_WAIT_MS)
      } catch {
        return ''
      } finally {
        if (!readMercadoPagoDeviceSessionId()) {
          securityScriptPromise = null
        }
      }
    })()
  }

  return securityScriptPromise
}

/** Recarrega security.js quando a sessão não foi gerada (ex.: script bloqueado no 1º load). */
export async function reloadMercadoPagoSecurityScript(view = 'checkout') {
  securityScriptPromise = null

  document
    .querySelectorAll('script[data-mp-security="v2"], script[src*="mercadopago.com/v2/security.js"]')
    .forEach((node) => node.remove())

  const hidden = document.getElementById('deviceId')
  if (hidden instanceof HTMLInputElement) {
    hidden.value = ''
  }

  return loadMercadoPagoSecurityScript(view)
}

/** Pré-carrega antifraude ao abrir checkout (antes do formulário de cartão). */
export function preloadMercadoPagoAntifraud(publicKey) {
  const key = String(publicKey || '').trim()
  if (!key || !isLikelyMercadoPagoPublicKey(key)) {
    return Promise.resolve('')
  }
  ensureMercadoPagoDeviceIdElement()
  void loadMercadoPagoSecurityScript('checkout')
  return requireMercadoPagoDeviceSession(key, 8000)
}

export function loadMercadoPagoSdk() {
  if (typeof window !== 'undefined' && window.MercadoPago) {
    return Promise.resolve(window.MercadoPago)
  }
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const finish = () => waitForMercadoPagoGlobal().then(resolve).catch(reject)
      const existing = document.querySelector('script[data-mp-sdk="v2"]')
      if (existing) {
        if (existing.getAttribute('data-loaded') === '1' || window.MercadoPago) {
          finish()
          return
        }
        existing.addEventListener('load', () => {
          existing.setAttribute('data-loaded', '1')
          finish()
        }, { once: true })
        existing.addEventListener('error', () => reject(new Error('Mercado Pago SDK failed to load')), { once: true })
        finish()
        return
      }
      const script = document.createElement('script')
      script.src = SDK_URL
      script.async = true
      script.dataset.mpSdk = 'v2'
      script.onload = () => {
        script.setAttribute('data-loaded', '1')
        finish()
      }
      script.onerror = () => reject(new Error('Mercado Pago SDK failed to load'))
      document.head.appendChild(script)
    }).catch((error) => {
      sdkPromise = null
      throw error
    })
  }
  return sdkPromise
}

export async function getMercadoPagoClient(publicKey, locale = 'pt-BR') {
  const key = String(publicKey || '').trim()
  if (!key) throw new Error('Mercado Pago public key is required')
  const cacheKey = `${key}:${locale}`
  if (mpInstances.has(cacheKey)) return mpInstances.get(cacheKey)
  const MercadoPago = await loadMercadoPagoSdk()
  const mp = new MercadoPago(key, {
    locale,
    advancedFraudPrevention: true,
  })
  mpInstances.set(cacheKey, mp)
  return mp
}

/** Nova instância a cada chamada — evita estado sujo do CardForm / Secure Fields. */
export async function createFreshMercadoPagoClient(publicKey, locale = 'pt-BR') {
  const key = String(publicKey || '').trim()
  if (!key) throw new Error('Mercado Pago public key is required')
  const MercadoPago = await loadMercadoPagoSdk()
  return new MercadoPago(key, {
    locale,
    advancedFraudPrevention: true,
  })
}

export function isLikelyMercadoPagoPublicKey(key) {
  const trimmed = String(key || '').trim()
  if (!/^(TEST-|APP_USR-)/.test(trimmed) || trimmed.length < 16) return false
  return !isLikelyMercadoPagoAccessToken(trimmed)
}

export function isLikelyMercadoPagoAccessToken(key) {
  const trimmed = String(key || '').trim()
  if (!trimmed) return false
  if (trimmed.length >= 72) return true
  return /^(TEST-|APP_USR-)\d{10,}/.test(trimmed)
}

export function resolveMercadoPagoCardErrorKey(error) {
  const cause = String(error?.cause || error?.type || '').toLowerCase()
  if (cause.includes('payment_method') || cause === 'get_payment_methods_failed') {
    return 'cardBrickGetPaymentMethodsFailed'
  }
  if (cause.includes('fields_setup')) return 'cardBrickSecureFieldsFailed'
  return 'cardBrickError'
}

/**
 * Aguarda sessão antifraude — obrigatória em produção (APP_USR).
 * Polling leve + reload do security.js se necessário (evita espera de 40s+).
 */
export async function requireMercadoPagoDeviceSession(publicKey, timeoutMs = 12000) {
  const key = String(publicKey || '').trim()
  if (!key || !isLikelyMercadoPagoPublicKey(key)) {
    return ''
  }

  ensureMercadoPagoDeviceIdElement()

  let sessionId = readMercadoPagoDeviceSessionId()
  if (sessionId) return sessionId

  void loadMercadoPagoSecurityScript('checkout')

  const deadline = Date.now() + timeoutMs
  let reloaded = false

  while (Date.now() < deadline) {
    sessionId = readMercadoPagoDeviceSessionId()
    if (sessionId) return sessionId

    if (!reloaded && Date.now() > deadline - timeoutMs + 4000) {
      await reloadMercadoPagoSecurityScript('checkout')
      reloaded = true
    }

    await new Promise((resolve) => setTimeout(resolve, 120))
  }

  return readMercadoPagoDeviceSessionId()
}

/**
 * Estilos dos iframes PCI — SEMPRE texto escuro (iframe MP tem fundo branco).
 */
export function getMercadoPagoIframeFieldStyle() {
  return {
    fontSize: '16px',
    fontWeight: '400',
    color: '#171717',
    placeholderColor: '#737373',
    padding: '0 12px',
  }
}
